import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Warning, Trash } from "@phosphor-icons/react";
import { authFetch, useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const CONFIRM_TOKEN = "DELETE MY ACCOUNT";

export default function DeleteAccountSection() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    const canDelete = confirmText.trim().toUpperCase() === CONFIRM_TOKEN;

    const doDelete = async () => {
        if (!canDelete) return;
        setDeleting(true);
        try {
            await authFetch("delete", "/profile");
            toast.success("Your account has been permanently deleted.");
            logout();
            navigate("/", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete account. Please try again.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <div className="glass p-6 border border-red-500/20" data-testid="delete-account-section">
                <div className="flex items-start gap-3">
                    <Warning size={18} weight="duotone" className="text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="label-tracking text-red-300">Danger Zone</p>
                        <h3 className="font-serif text-xl mt-1">Delete Account</h3>
                        <p className="text-sm text-muted mt-2 leading-relaxed">
                            Permanently erase your profile, photos, matches, messages, and cached readings.
                            This cannot be undone.
                        </p>
                        <button
                            onClick={() => setOpen(true)}
                            className="mt-4 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
                            data-testid="delete-account-open"
                        >
                            <Trash size={14} weight="duotone" /> Delete My Account
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                        onClick={() => !deleting && setOpen(false)}
                        data-testid="delete-account-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => e.stopPropagation()}
                            className="glass-gold p-6 sm:p-8 max-w-md w-full"
                        >
                            <div className="flex items-start gap-3 mb-4">
                                <Warning size={22} weight="duotone" className="text-red-400 flex-shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-serif text-2xl">Permanently delete your account?</h3>
                                    <p className="text-sm text-muted mt-2 leading-relaxed">
                                        This will immediately remove your profile, all uploaded photos, matches,
                                        messages, and cached horoscopes. Your subscription (if any) will be cancelled at
                                        the end of the current billing cycle and is <strong>non-refundable</strong>.
                                    </p>
                                </div>
                            </div>
                            <label className="block mt-4">
                                <span className="text-xs label-tracking text-muted">
                                    Type <span className="text-red-300 font-mono">{CONFIRM_TOKEN}</span> to confirm
                                </span>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    className="mt-2 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-red-400 outline-none font-mono text-sm"
                                    placeholder={CONFIRM_TOKEN}
                                    autoFocus
                                    data-testid="delete-account-confirm-input"
                                />
                            </label>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => { setOpen(false); setConfirmText(""); }}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-3 rounded-full border border-white/10 text-muted hover:text-ink hover:border-white/30 transition-colors disabled:opacity-50"
                                    data-testid="delete-account-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={doDelete}
                                    disabled={!canDelete || deleting}
                                    className="flex-1 px-4 py-3 rounded-full bg-red-500/20 border border-red-500/60 text-red-200 hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    data-testid="delete-account-confirm"
                                >
                                    {deleting ? "Deleting..." : "Delete Forever"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
